import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Flame, Send } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { DifficultyBadge } from '@/components/ui/DifficultyBadge';
import { Heatmap } from '@/components/profile/Heatmap';
import { meApi } from '@/api/endpoints';
import { mapDifficulty } from '@/api/mappers';
import type { MeStatsResponse, MeSubmissionResponse } from '@/api/types';
import { useAuthStore } from '@/store/auth';
import { useLocalized } from '@/hooks/useLocalized';
import { formatDate, formatNumber, timeAgo } from '@/lib/format';
import type { Difficulty, LocalizedText } from '@/types';
import { useSeo } from '@/hooks/useSeo';

const DIFF_COLORS: Record<Difficulty, string> = {
  easy: 'var(--easy)',
  medium: 'var(--medium)',
  hard: 'var(--hard)',
};

const API_DIFFICULTY: Record<Difficulty, 'EASY' | 'MEDIUM' | 'HARD'> = {
  easy: 'EASY',
  medium: 'MEDIUM',
  hard: 'HARD',
};

const STATUS_COLOR: Record<string, string> = {
  ACCEPTED: 'var(--success)',
  WRONG_ANSWER: 'var(--error)',
  RUNTIME_ERROR: 'var(--warning)',
  SYNTAX_ERROR: 'var(--warning)',
  TIME_LIMIT: 'var(--warning)',
  INTERNAL_ERROR: 'var(--error)',
  PENDING: 'var(--text-muted)',
  RUNNING: 'var(--info)',
};

export function Profile() {
  // Shaxsiy/JS-only sahifa: indekslanmaydi; oldingi sahifaning JSON-LD i tozalanadi
  useSeo({ title: "Profil — SQLMaster.uz", noindex: true });
  const { t } = useTranslation();
  const { l, lang } = useLocalized();
  const user = useAuthStore((s) => s.user);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  const [stats, setStats] = useState<MeStatsResponse | null>(null);
  const [submissions, setSubmissions] = useState<MeSubmissionResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(false);

    Promise.all([meApi.stats(), meApi.submissions(0, 10)])
      .then(([s, subs]) => {
        setStats(s.data);
        setSubmissions(subs.data.content);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(load, [load]);

  // Kirmagan foydalanuvchiga soxta profil ko'rsatmaymiz
  if (!isAuthenticated || !user) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-4 text-center">
        <h1 className="mb-2 text-2xl font-bold">{t('profile.loginTitle')}</h1>
        <p className="mb-6 text-content-secondary">{t('profile.loginSubtitle')}</p>
        <div className="flex gap-2">
          <Link to="/login" className="btn btn-primary px-5 py-2.5 text-sm">
            {t('nav.login')}
          </Link>
          <Link to="/register" className="btn btn-secondary px-5 py-2.5 text-sm">
            {t('nav.register')}
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-8xl px-4 py-16 text-center text-content-secondary">
        {t('common.loading')}
      </div>
    );
  }

  if (error || !stats) {
    return (
      <div className="mx-auto max-w-8xl px-4 py-16 text-center">
        <p className="text-[var(--error)]">{t('profile.loadError')}</p>
        <button onClick={load} className="mt-3 text-sm font-medium text-brand-600 hover:underline">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-8xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <Card className="mb-6 p-6">
        <div className="flex flex-col items-start gap-5 sm:flex-row sm:items-center">
          <div
            className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full text-2xl font-bold text-white"
            style={{ background: 'linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))' }}
          >
            {user.name.charAt(0).toUpperCase()}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold">{user.name}</h1>
              {user.isPremium && (
                <span className="rounded bg-[var(--medium)]/15 px-2 py-0.5 text-xs font-semibold text-[var(--medium)]">
                  Premium
                  {user.premiumExpiresAt && (
                    <span className="font-normal">
                      {' · '}
                      {t('pricing.activeUntil', { date: formatDate(user.premiumExpiresAt, lang) })}
                    </span>
                  )}
                </span>
              )}
            </div>
            <p className="text-content-muted">@{user.username}</p>
            <p className="mt-1 text-sm text-content-muted">
              {t('profile.joined', { date: formatDate(user.joinedAt, lang) })}
            </p>
          </div>

          <div className="flex gap-6">
            <Stat
              icon={<CheckCircle2 className="h-4 w-4 text-[var(--success)]" />}
              value={formatNumber(stats.totalSolved, lang)}
              label={t('profile.totalSolved')}
            />
            <Stat
              icon={<Send className="h-4 w-4" />}
              value={formatNumber(stats.totalSubmissions, lang)}
              label={t('profile.submissions')}
            />
            <Stat
              icon={<Flame className="h-4 w-4 text-[var(--medium)]" />}
              value={String(stats.currentStreak)}
              label={t('profile.streak')}
            />
          </div>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column */}
        <div className="space-y-6 lg:col-span-1">
          {/* Solved */}
          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">{t('profile.solvedTitle')}</h2>
            <div className="mb-4 text-center">
              <div className="tnum text-4xl font-bold text-gradient">{stats.totalSolved}</div>
              <div className="text-sm text-content-muted">
                {t('profile.outOf', { total: stats.totalProblems })}
              </div>
            </div>
            <div className="space-y-3">
              {(['easy', 'medium', 'hard'] as Difficulty[]).map((d) => {
                const s = stats.byDifficulty[API_DIFFICULTY[d]] ?? { solved: 0, total: 0 };
                return (
                  <div key={d}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span style={{ color: DIFF_COLORS[d] }}>{t(`difficulty.${d}`)}</span>
                      <span className="tnum text-content-secondary">
                        {s.solved} / {s.total}
                      </span>
                    </div>
                    <ProgressBar value={s.solved} max={s.total} color={DIFF_COLORS[d]} />
                  </div>
                );
              })}
            </div>
          </Card>

          {/* Streak */}
          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">{t('profile.streakTitle')}</h2>
            <div className="grid grid-cols-2 gap-4 text-center">
              <div>
                <div className="tnum text-3xl font-bold text-[var(--medium)]">{stats.currentStreak}</div>
                <div className="text-xs text-content-muted">{t('profile.currentStreak')}</div>
              </div>
              <div>
                <div className="tnum text-3xl font-bold">{stats.longestStreak}</div>
                <div className="text-xs text-content-muted">{t('profile.longestStreak')}</div>
              </div>
            </div>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-6 lg:col-span-2">
          {/* Activity */}
          <Card className="p-6">
            <h2 className="mb-4 text-lg font-semibold">{t('profile.activity')}</h2>
            <Heatmap activity={stats.activity} />
          </Card>

          {/* Topic mastery */}
          {stats.topicMastery.length > 0 && (
            <Card className="p-6">
              <h2 className="mb-4 text-lg font-semibold">{t('profile.topicMastery')}</h2>
              <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
                {stats.topicMastery.map((tm) => (
                  <div key={tm.topic}>
                    <div className="mb-1 flex items-center justify-between text-sm">
                      <span className="text-content">{tm.topic}</span>
                      <span className="tnum text-content-muted">
                        {tm.solved}/{tm.total}
                      </span>
                    </div>
                    <ProgressBar value={tm.solved} max={tm.total} />
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Recent submissions */}
          <Card className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('profile.recentSubmissions')}</h2>
              <Link to="/problems" className="text-sm text-brand-600 hover:underline">
                {t('profile.viewAll')}
              </Link>
            </div>

            {submissions.length === 0 ? (
              <p className="py-8 text-center text-sm text-content-muted">
                {t('profile.noSubmissions')}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-muted">
                      <th className="py-2 pr-2">{t('profile.colProblem')}</th>
                      <th className="px-2 py-2">{t('profile.colStatus')}</th>
                      <th className="hidden px-2 py-2 sm:table-cell">{t('profile.colDialect')}</th>
                      <th className="hidden px-2 py-2 text-right md:table-cell">{t('profile.colRuntime')}</th>
                      <th className="px-2 py-2 text-right">{t('profile.colWhen')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {submissions.map((s) => {
                      const title: LocalizedText = {
                        uz: s.problemTitleUz,
                        ru: s.problemTitleRu,
                        en: s.problemTitleEn,
                      };
                      return (
                        <tr key={s.id} className="border-b border-line last:border-0">
                          <td className="py-2.5 pr-2">
                            <Link
                              to={`/problems/${s.problemSlug}`}
                              className="flex items-center gap-2 font-medium hover:text-brand-600"
                            >
                              <DifficultyBadge difficulty={mapDifficulty(s.difficulty)} />
                              <span className="truncate">{l(title)}</span>
                            </Link>
                          </td>
                          <td className="px-2 py-2.5">
                            <span
                              className="text-xs font-medium"
                              style={{ color: STATUS_COLOR[s.status] ?? 'var(--text-muted)' }}
                            >
                              {t(`problem.verdict.${s.status.toLowerCase()}`, s.status)}
                            </span>
                          </td>
                          <td className="hidden px-2 py-2.5 font-mono text-xs text-content-secondary sm:table-cell">
                            {s.dialect}
                          </td>
                          <td className="hidden px-2 py-2.5 text-right font-mono text-xs text-content-secondary md:table-cell">
                            {s.runtimeMs != null ? `${s.runtimeMs} ms` : '—'}
                          </td>
                          <td className="px-2 py-2.5 text-right text-xs text-content-muted">
                            {timeAgo(s.createdAt, lang)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function Stat({ icon, value, label }: { icon: React.ReactNode; value: string; label: string }) {
  return (
    <div className="text-center">
      <div className="flex items-center justify-center gap-1 text-content-muted">{icon}</div>
      <div className="tnum text-lg font-bold">{value}</div>
      <div className="text-xs text-content-muted">{label}</div>
    </div>
  );
}
